import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PERMISSIONS,
  PurchaseOrderStatus,
  type PurchaseOrderDetail,
  type PurchaseOrderLineSummary,
  type PurchaseReceiptSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCancelPurchaseOrderMutation,
  useClosePurchaseOrderMutation,
  usePlacePurchaseOrderMutation,
  usePurchaseOrderQuery,
  useUpdatePurchaseOrderMutation,
} from '../api/purchase-orders.api';
import { usePurchaseOrderReceiptsQuery } from '../api/purchase-receipts.api';
import { PurchaseOrderStatusBadge } from '../components/PurchaseOrderStatusBadge';

type Action = 'place' | 'cancel' | 'close' | null;

/** Anything still expected from the supplier on this line. */
function remainingOf(line: PurchaseOrderLineSummary): number {
  return line.quantityRemaining;
}

function Detail({ label, value, ltr = false }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm" dir={ltr ? 'ltr' : 'auto'}>
        {value}
      </dd>
    </div>
  );
}

/**
 * One purchase order: what was asked for, what has arrived, and what can still be done about it.
 *
 * Every action here is the server's rule rendered, not re-decided: receiving appears while the
 * order is open, cancelling only while nothing has arrived, and closing the remainder only once
 * part of it has. The screen never offers an action the API would refuse — and never at the wrong
 * branch, which it says plainly rather than failing at the last click.
 */
export function PurchaseOrderDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { can } = usePermissions();
  const { activeBranch } = useBranch();
  const { format } = useCurrency();

  const [action, setAction] = useState<Action>(null);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [supplierReference, setSupplierReference] = useState('');
  const [notes, setNotes] = useState('');

  const { data, isPending, isError, error, refetch } = usePurchaseOrderQuery(orderId);
  const receipts = usePurchaseOrderReceiptsQuery(orderId);

  const place = usePlacePurchaseOrderMutation();
  const cancel = useCancelPurchaseOrderMutation();
  const close = useClosePurchaseOrderMutation();
  const update = useUpdatePurchaseOrderMutation();

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('purchasing.orders.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const order: PurchaseOrderDetail = data;
  const atOrderBranch = activeBranch?.id === order.branchId;
  const isOpen =
    order.status === PurchaseOrderStatus.ORDERED ||
    order.status === PurchaseOrderStatus.PARTIALLY_RECEIVED;
  const hasRemaining = order.lines.some((line) => remainingOf(line) > 0);
  const nothingReceived = order.lines.every((line) => line.quantityReceived === 0);

  const canPlace =
    can(PERMISSIONS.PURCHASING_APPROVE) && order.status === PurchaseOrderStatus.DRAFT;
  const canEditDraft =
    can(PERMISSIONS.PURCHASING_UPDATE) && order.status === PurchaseOrderStatus.DRAFT;
  const canEditHeader = can(PERMISSIONS.PURCHASING_UPDATE) && isOpen;
  const canReceive = can(PERMISSIONS.PURCHASING_RECEIVE) && isOpen && hasRemaining;
  const canCancel =
    can(PERMISSIONS.PURCHASING_CANCEL) &&
    nothingReceived &&
    (order.status === PurchaseOrderStatus.DRAFT || order.status === PurchaseOrderStatus.ORDERED);
  const canClose =
    can(PERMISSIONS.PURCHASING_CANCEL) &&
    order.status === PurchaseOrderStatus.PARTIALLY_RECEIVED &&
    hasRemaining;

  const busy = place.isPending || cancel.isPending || close.isPending;
  const actionError =
    apiErrorMessage(place.error) ?? apiErrorMessage(cancel.error) ?? apiErrorMessage(close.error);

  const openHeaderEditor = () => {
    setExpectedDeliveryDate(order.expectedDeliveryDate ?? '');
    setSupplierReference(order.supplierReference ?? '');
    setNotes(order.notes ?? '');
    setIsEditingHeader(true);
  };

  const confirmAction = () => {
    if (action === 'place') {
      place.mutate(
        { id: order.id, version: order.version },
        {
          onSuccess: () => {
            setFeedback(t('purchasing.orders.placed', { reference: order.reference }));
            setAction(null);
          },
        },
      );
      return;
    }

    if (!reason.trim()) return;

    const payload = { id: order.id, version: order.version, reason: reason.trim() };
    const done = (message: string) => () => {
      setFeedback(message);
      setAction(null);
      setReason('');
    };

    if (action === 'cancel') {
      cancel.mutate(payload, { onSuccess: done(t('purchasing.orders.cancelled')) });
      return;
    }
    close.mutate(payload, { onSuccess: done(t('purchasing.orders.closed')) });
  };

  const lineColumns: Column<PurchaseOrderLineSummary>[] = [
    {
      key: 'item',
      header: t('purchasing.orders.fields.item'),
      primary: true,
      render: (line) => (
        <span className="flex flex-col">
          <span className="text-ink text-sm" dir="auto">
            {line.variantName ?? line.productName}
          </span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {line.sku}
          </span>
        </span>
      ),
    },
    {
      key: 'ordered',
      header: t('purchasing.orders.fields.ordered'),
      align: 'end',
      render: (line) => (
        <span className="text-ink" dir="ltr">
          {line.quantityOrdered}
        </span>
      ),
    },
    {
      key: 'received',
      header: t('purchasing.orders.fields.received'),
      align: 'end',
      render: (line) => (
        <span className="text-ink" dir="ltr">
          {line.quantityReceived}
        </span>
      ),
    },
    {
      key: 'cancelled',
      header: t('purchasing.orders.fields.cancelled'),
      align: 'end',
      render: (line) => (
        <span className="text-muted" dir="ltr">
          {line.quantityCancelled}
        </span>
      ),
    },
    {
      key: 'remaining',
      header: t('purchasing.orders.fields.remaining'),
      align: 'end',
      render: (line) => (
        <span className="text-ink font-medium" dir="ltr">
          {remainingOf(line)}
        </span>
      ),
    },
    {
      key: 'billed',
      header: t('purchasing.invoices.invoicedOnOrder'),
      align: 'end',
      render: (line) => (
        <span className="flex flex-col items-end text-xs">
          <span className="text-ink" dir="ltr">
            {line.quantityInvoiced}
          </span>
          {line.quantityInvoiceable > 0 ? (
            <span className="text-muted" dir="ltr">
              {t('purchasing.invoices.leftToBill', { count: line.quantityInvoiceable })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'cost',
      header: t('purchasing.orders.fields.agreedUnitCost'),
      align: 'end',
      render: (line) => (
        <span className="text-muted" dir="ltr">
          {format(line.agreedUnitCostMinor)}
        </span>
      ),
    },
    {
      key: 'tax',
      header: t('purchasing.orders.fields.taxProfile'),
      render: (line) => (
        <span className="flex flex-col text-xs">
          <span className="text-ink">{t(`sales.taxTreatments.${line.taxTreatment}`)}</span>
          <span className="text-muted" dir="ltr">
            {t('purchasing.orders.fields.taxRate', { percent: line.taxRateBasisPoints / 100 })}
          </span>
        </span>
      ),
    },
    {
      key: 'total',
      header: t('purchasing.orders.fields.lineTotal'),
      align: 'end',
      render: (line) => (
        <span className="flex flex-col text-xs">
          <span className="text-ink" dir="ltr">
            {format(line.totalMinor)}
          </span>
          <span className="text-muted" dir="ltr">
            {t('purchasing.orders.fields.netAndTax', {
              net: format(line.netMinor),
              tax: format(line.taxMinor),
            })}
          </span>
        </span>
      ),
    },
  ];

  const receiptColumns: Column<PurchaseReceiptSummary>[] = [
    {
      key: 'reference',
      header: t('purchasing.receipts.fields.reference'),
      primary: true,
      render: (receipt) => (
        <span className="text-ink font-mono text-sm font-medium" dir="ltr">
          {receipt.reference}
        </span>
      ),
    },
    {
      key: 'receivedOn',
      header: t('purchasing.receipts.fields.receivedOn'),
      render: (receipt) => (
        <span className="text-muted" dir="ltr">
          {receipt.receivedOn}
        </span>
      ),
    },
    {
      key: 'quantity',
      header: t('purchasing.receipts.fields.quantity'),
      align: 'end',
      render: (receipt) => (
        <span className="text-ink" dir="ltr">
          {receipt.totalQuantity}
        </span>
      ),
    },
    {
      key: 'lines',
      header: t('purchasing.receipts.fields.lines'),
      align: 'end',
      render: (receipt) => (
        <span className="text-muted" dir="ltr">
          {receipt.lineCount}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (receipt) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/receipts/${receipt.id}`)}
          aria-label={t('purchasing.receipts.openNamed', { reference: receipt.reference })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={order.reference}
        subtitle={order.supplierName}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canEditDraft ? (
              <Button
                variant="secondary"
                onClick={() => void navigate(`/purchasing/orders/${order.id}/edit`)}
              >
                {t('purchasing.orders.editDraft')}
              </Button>
            ) : null}
            {canEditHeader ? (
              <Button variant="secondary" onClick={openHeaderEditor}>
                {t('purchasing.orders.editDetails')}
              </Button>
            ) : null}
            {canReceive ? (
              <Button
                disabled={!atOrderBranch}
                onClick={() => void navigate(`/purchasing/orders/${order.id}/receive`)}
              >
                {t('purchasing.receipts.receive')}
              </Button>
            ) : null}
            {canPlace ? (
              <Button onClick={() => setAction('place')}>{t('purchasing.orders.place')}</Button>
            ) : null}
            {canClose ? (
              <Button variant="secondary" onClick={() => setAction('close')}>
                {t('purchasing.orders.close')}
              </Button>
            ) : null}
            {canCancel ? (
              <Button variant="danger" onClick={() => setAction('cancel')}>
                {t('purchasing.orders.cancel')}
              </Button>
            ) : null}
          </div>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {actionError ? <Alert tone="error">{actionError}</Alert> : null}
      {canReceive && !atOrderBranch ? (
        <Alert tone="warning">
          {t('purchasing.receipts.wrongBranch', { branch: order.branchName })}
        </Alert>
      ) : null}
      {order.status === PurchaseOrderStatus.DRAFT ? (
        <Alert tone="info">{t('purchasing.orders.draftNotice')}</Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('purchasing.orders.summary')}
          action={<PurchaseOrderStatusBadge status={order.status} />}
        />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail label={t('purchasing.orders.fields.supplier')} value={order.supplierName} />
            <Detail label={t('purchasing.orders.fields.branch')} value={order.branchName} />
            <Detail label={t('purchasing.orders.fields.currency')} value={order.currencyCode} ltr />
            <Detail
              label={t('purchasing.orders.fields.orderedOn')}
              value={order.orderedOn ?? '—'}
              ltr
            />
            <Detail
              label={t('purchasing.orders.fields.expectedDeliveryDate')}
              value={order.expectedDeliveryDate ?? '—'}
              ltr
            />
            <Detail
              label={t('purchasing.orders.fields.supplierReference')}
              value={order.supplierReference ?? '—'}
            />
            <Detail
              label={t('purchasing.orders.fields.orderedBy')}
              value={order.orderedByName ?? '—'}
            />
            <Detail label={t('purchasing.orders.fields.notes')} value={order.notes ?? '—'} />
            {order.cancelledAt ? (
              <Detail
                label={t('purchasing.orders.fields.cancelledReason')}
                value={order.cancelledReason ?? '—'}
              />
            ) : null}
            {order.closedAt ? (
              <Detail
                label={t('purchasing.orders.fields.closedReason')}
                value={order.closedReason ?? '—'}
              />
            ) : null}
          </dl>

          <div className="border-line mt-4 flex flex-wrap justify-end gap-6 border-t pt-4 text-sm">
            <span className="text-muted">
              {t('purchasing.orders.fields.subtotal')}{' '}
              <span className="text-ink font-medium" dir="ltr">
                {format(order.subtotalMinor)}
              </span>
            </span>
            <span className="text-muted">
              {t('purchasing.orders.fields.tax')}{' '}
              <span className="text-ink font-medium" dir="ltr">
                {format(order.taxMinor)}
              </span>
            </span>
            <span className="text-ink font-semibold">
              {t('purchasing.orders.fields.total')}{' '}
              <span dir="ltr">{format(order.totalMinor)}</span>
            </span>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('purchasing.orders.linesSection')}
          subtitle={t('purchasing.orders.taxExclusive')}
        />
        <DataTable
          caption={t('purchasing.orders.linesSection')}
          columns={lineColumns}
          rows={order.lines}
          rowKey={(line) => line.id}
          emptyTitle={t('purchasing.orders.noLines')}
        />
      </Card>

      <Card>
        <CardHeader
          title={t('purchasing.receipts.historyTitle')}
          subtitle={t('purchasing.receipts.historyHint')}
        />
        <DataTable
          caption={t('purchasing.receipts.historyTitle')}
          columns={receiptColumns}
          rows={receipts.data?.items}
          rowKey={(receipt) => receipt.id}
          isLoading={receipts.isPending}
          isError={receipts.isError}
          errorMessage={apiErrorMessage(receipts.error) ?? t('purchasing.receipts.loadError')}
          onRetry={() => void receipts.refetch()}
          emptyTitle={t('purchasing.receipts.noneYet')}
        />
      </Card>

      <Modal
        isOpen={action !== null}
        onClose={() => {
          setAction(null);
          setReason('');
        }}
        title={
          action === 'place'
            ? t('purchasing.orders.placeTitle')
            : action === 'close'
              ? t('purchasing.orders.closeTitle')
              : t('purchasing.orders.cancelTitle')
        }
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setAction(null);
                setReason('');
              }}
              disabled={busy}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant={action === 'cancel' ? 'danger' : 'primary'}
              onClick={confirmAction}
              isLoading={busy}
              disabled={action !== 'place' && !reason.trim()}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          {action === 'place' ? (
            <>
              <p className="text-ink">{t('purchasing.orders.placeMessage')}</p>
              <dl className="bg-canvas grid gap-2 rounded-lg p-3 sm:grid-cols-2">
                <Detail
                  label={t('purchasing.orders.fields.reference')}
                  value={order.reference}
                  ltr
                />
                <Detail label={t('purchasing.orders.fields.supplier')} value={order.supplierName} />
                <Detail label={t('purchasing.orders.fields.branch')} value={order.branchName} />
                <Detail
                  label={t('purchasing.orders.fields.lines')}
                  value={String(order.lineCount)}
                  ltr
                />
                <Detail
                  label={t('purchasing.orders.fields.total')}
                  value={format(order.totalMinor)}
                  ltr
                />
              </dl>
              <p className="text-muted">{t('purchasing.orders.placeFreezes')}</p>
            </>
          ) : (
            <>
              <p className="text-ink">
                {action === 'close'
                  ? t('purchasing.orders.closeMessage')
                  : t('purchasing.orders.cancelMessage')}
              </p>
              <Input
                label={t('purchasing.orders.fields.reason')}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                required
              />
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isEditingHeader}
        onClose={() => setIsEditingHeader(false)}
        title={t('purchasing.orders.editDetails')}
        description={t('purchasing.orders.editDetailsHint')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsEditingHeader(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              isLoading={update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    id: order.id,
                    version: order.version,
                    ...(expectedDeliveryDate ? { expectedDeliveryDate } : {}),
                    ...(supplierReference.trim()
                      ? { supplierReference: supplierReference.trim() }
                      : {}),
                    ...(notes.trim() ? { notes: notes.trim() } : {}),
                  },
                  {
                    onSuccess: () => {
                      setIsEditingHeader(false);
                      setFeedback(t('purchasing.orders.detailsSaved'));
                    },
                  },
                )
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {apiErrorMessage(update.error) ? (
            <Alert tone="error">{apiErrorMessage(update.error)}</Alert>
          ) : null}
          <Input
            type="date"
            label={t('purchasing.orders.fields.expectedDeliveryDate')}
            value={expectedDeliveryDate}
            onChange={(event) => setExpectedDeliveryDate(event.target.value)}
          />
          <Input
            label={t('purchasing.orders.fields.supplierReference')}
            value={supplierReference}
            onChange={(event) => setSupplierReference(event.target.value)}
          />
          <div>
            <label className="field-label" htmlFor="ordered-notes">
              {t('purchasing.orders.fields.notes')}
            </label>
            <textarea
              id="ordered-notes"
              rows={2}
              dir="auto"
              className="border-line focus:border-brand-500 focus:ring-brand-100 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4 focus:outline-none"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
